import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Handyman as HandymanIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ConfirmDialog from '../common/ConfirmDialog';
import FixFailedImportRowsDialog from './FixFailedImportRowsDialog';
import MergeDuplicateCustomersDialog from './MergeDuplicateCustomersDialog';
import {
  buildAutomaticMergeDecisions,
  buildFailingCustomersCsv,
  buildImportCustomersPayload,
  buildInitialDuplicateDecisions,
  findCustomerDuplicateGroups,
  parseFreshaCustomerCsv,
} from '../../utils/freshaCustomerCsv';
import StoreDataImportService from '../../services/storeDataImportService';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const FRESHA_CLIENTS_EXPORT_URL = 'https://partners.fresha.com/clients/list';

const uploadButtonSx = {
  borderColor: BRAND.accent,
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  height: 40,
  px: 2,
  '&:hover': { borderColor: BRAND.accentDark, backgroundColor: brandAlpha('accent', 0.08) },
  '&.Mui-disabled': {
    borderColor: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
};

const textActionSx = {
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  px: 0.5,
  minWidth: 0,
  '&:hover': {
    backgroundColor: 'transparent',
    color: BRAND.accentDark,
    textDecoration: 'underline',
  },
  '&.Mui-disabled': {
    color: 'rgba(0, 0, 0, 0.26)',
  },
};

const downloadTextFile = (content, fileName, mimeType = 'text/csv;charset=utf-8') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ImportCustomersStep = ({
  storeId,
  onCompleted,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [delimiter, setDelimiter] = useState(';');
  const [fileName, setFileName] = useState('');
  const [parseErrorKey, setParseErrorKey] = useState(null);
  const [fixOpen, setFixOpen] = useState(false);
  const [importReadyConfirmOpen, setImportReadyConfirmOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeGroups, setMergeGroups] = useState([]);
  const [mergeDecisions, setMergeDecisions] = useState({});
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const failedRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);
  const readyRows = useMemo(() => rows.filter((row) => row.errors.length === 0), [rows]);
  const warningCount = useMemo(
    () => rows.reduce((total, row) => total + (row.warnings?.length || 0), 0),
    [rows],
  );

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportResult(null);
    setImportError(null);
    setMergeDecisions({});
    setMergeGroups([]);

    try {
      const text = await file.text();
      const parsed = parseFreshaCustomerCsv(text);
      if (parsed.errorKey) {
        setRows([]);
        setHeaders([]);
        setFileName('');
        setParseErrorKey(parsed.errorKey);
        return;
      }

      setParseErrorKey(null);
      setFileName(file.name);
      setRows(parsed.rows);
      setHeaders(parsed.headers || []);
      setDelimiter(parsed.delimiter || ';');
    } catch {
      setRows([]);
      setHeaders([]);
      setFileName('');
      setParseErrorKey('dataImport.customers.errors.parseFailed');
    }
  };

  const handleSaveFixedRow = (updatedRow) => {
    setRows((current) => current.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
  };

  const runImport = async (decisions) => {
    setImporting(true);
    setImportError(null);
    try {
      const payload = buildImportCustomersPayload(rows, decisions);
      const result = await StoreDataImportService.importCustomers(storeId, payload);
      setImportResult(result);
      onCompleted?.(result);
    } catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.title
        || t('dataImport.customers.errors.importFailed');
      setImportError(message);
    } finally {
      setImporting(false);
      setMergeOpen(false);
      setImportReadyConfirmOpen(false);
    }
  };

  const startImportFlow = async () => {
    if (!readyRows.length || disabled) return;

    const groups = findCustomerDuplicateGroups(rows);
    if (groups.length > 0) {
      setMergeGroups(groups);
      setMergeDecisions(buildInitialDuplicateDecisions(groups));
      setMergeOpen(true);
      return;
    }

    await runImport({});
  };

  const handleImportClick = async () => {
    if (failedRows.length > 0 || readyRows.length === 0 || disabled) return;
    await startImportFlow();
  };

  const handleImportReadyConfirm = async () => {
    if (!readyRows.length || disabled) return;
    await startImportFlow();
  };

  const handleAutoResolveDuplicates = async () => {
    if (!readyRows.length || disabled) return;
    const decisions = buildAutomaticMergeDecisions(rows);
    await runImport(decisions);
  };

  const handleDownloadFailingCsv = () => {
    if (!failedRows.length) return;

    const csv = buildFailingCustomersCsv(failedRows, headers, delimiter);
    const baseName = (fileName || 'fresha-customers').replace(/\.csv$/i, '');
    downloadTextFile(csv, `${baseName}-failing-rows.csv`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: `1px solid ${brandAlpha('accent', 0.35)}`,
          background: BRAND.background,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 1 }}>
          {t('dataImport.customers.helper.title')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t('dataImport.customers.helper.intro')}
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
            {t('dataImport.customers.helper.step1')}
            {' '}
            <Link href={FRESHA_CLIENTS_EXPORT_URL} target="_blank" rel="noopener noreferrer">
              {t('dataImport.customers.helper.openFresha')}
              <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
            </Link>
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
            {t('dataImport.customers.helper.step2')}
          </Typography>
          <Typography component="li" variant="body2">
            {t('dataImport.customers.helper.step3')}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          disabled={disabled || importing || Boolean(importResult)}
          sx={uploadButtonSx}
        >
          {t('dataImport.customers.uploadButton')}
          <input hidden type="file" accept=".csv,text/csv" onChange={handleFileChange} />
        </Button>
        {fileName && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('dataImport.customers.selectedFile', { fileName })}
          </Typography>
        )}
      </Box>

      {parseErrorKey && <Alert severity="error">{t(parseErrorKey)}</Alert>}
      {importError && <Alert severity="error">{importError}</Alert>}

      {rows.length > 0 && !importResult && (
        <>
          <Alert severity={failedRows.length ? 'warning' : 'success'}>
            {t('dataImport.customers.summary', {
              total: rows.length,
              ready: readyRows.length,
              failed: failedRows.length,
              warnings: warningCount,
            })}
          </Alert>

          {failedRows.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('dataImport.customers.failedTitle')}
              </Typography>
              {failedRows.slice(0, 8).map((row) => (
                <Typography key={row.id} variant="body2">
                  #{row.rowNumber}: {row.errors.map((error) => t(error.key)).join(', ')}
                </Typography>
              ))}
              {failedRows.length > 8 && (
                <Typography variant="body2" color="text.secondary">
                  {t('dataImport.customers.moreFailures', { count: failedRows.length - 8 })}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 2,
                  mt: 0.5,
                }}
              >
                <Button
                  variant="text"
                  startIcon={<HandymanIcon />}
                  onClick={() => setFixOpen(true)}
                  sx={textActionSx}
                >
                  {t('dataImport.customers.fixFailingRows')}
                </Button>
                <Button
                  variant="text"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadFailingCsv}
                  sx={textActionSx}
                >
                  {t('dataImport.customers.downloadFailingRows')}
                </Button>
                <PrimaryActionButton
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setImportReadyConfirmOpen(true)}
                  disabled={!readyRows.length || importing}
                >
                  {t('dataImport.customers.importReadyRows')}
                </PrimaryActionButton>
              </Box>
            </Box>
          )}

          {warningCount > 0 && (
            <Alert severity="info">
              {t('dataImport.customers.warningsNotice', { count: warningCount })}
            </Alert>
          )}

          {failedRows.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <PrimaryActionButton
                onClick={handleImportClick}
                disabled={disabled || importing || readyRows.length === 0}
              >
                {importing ? t('dataImport.customers.importing') : t('dataImport.customers.importButton')}
              </PrimaryActionButton>
            </Box>
          )}
        </>
      )}

      {importResult && (
        <Alert severity="success">
          {t('dataImport.customers.importSuccess', {
            created: importResult.createdCount,
            assigned: importResult.assignedCount,
            alreadyInStore: importResult.alreadyInStoreCount,
          })}
        </Alert>
      )}

      <FixFailedImportRowsDialog
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        rows={failedRows}
        onSaveRow={handleSaveFixedRow}
      />

      <ConfirmDialog
        open={importReadyConfirmOpen}
        onClose={() => setImportReadyConfirmOpen(false)}
        onConfirm={handleImportReadyConfirm}
        title={t('dataImport.customers.importReadyConfirmTitle')}
        message={t('dataImport.customers.importReadyConfirmMessage', {
          failed: failedRows.length,
          ready: readyRows.length,
        })}
        confirmText={t('dataImport.customers.importReadyConfirmAction')}
        confirmColor="primary"
        loading={importing}
      />

      <MergeDuplicateCustomersDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        groups={mergeGroups}
        decisions={mergeDecisions}
        onChangeDecision={(groupId, decision) => {
          setMergeDecisions((current) => ({ ...current, [groupId]: decision }));
        }}
        onConfirm={() => runImport(mergeDecisions)}
        onAutoResolve={handleAutoResolveDuplicates}
        importing={importing}
      />
    </Box>
  );
};

export default ImportCustomersStep;
